import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Edit3, Info, Lock, Plus, RefreshCw, Trash2 } from 'lucide-react';
import {
  backofficeBenefitTypeApi,
  extractErrorMessage,
  formatNumber,
} from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type {
  BenefitAudience,
  BenefitTypeAdminDto,
  BenefitTypeRequestDto,
  BenefitValueShape,
} from '@kardume/types';
import {
  BENEFIT_AUDIENCE_LABELS,
  BENEFIT_LABEL_PLACEHOLDERS,
  BENEFIT_TYPE_ERROR_MESSAGES,
  BENEFIT_VALUE_SHAPE_HELP,
  BENEFIT_VALUE_SHAPE_LABELS,
  BENEFIT_VALUE_SHAPE_REQUIREMENTS,
} from '@kardume/types';
import {
  ActionButton,
  Badge,
  Button,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatCard,
  Switch,
  Table,
  TableContainer,
  TextInput,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';

/**
 * O catálogo de tipos de benefício (card #361; motor do card #188).
 *
 * ## A fronteira que esta tela desenha
 *
 * O motor separa **dado** de **código**, e a tela existe para tornar essa linha
 * visível a quem opera:
 *
 * - é DADO o tipo: como se chama, o que o formulário do lojista exige, a frase
 *   que o cliente lê, o ícone, o preset de público. Abrir "entrada VIP" é um
 *   `INSERT` feito aqui, sem deploy;
 * - é CÓDIGO a **forma de calcular** (`valueShape`): quanto vale, sobre que
 *   base, com que teto. Cinco formas, cada uma com aritmética própria e
 *   testada.
 *
 * Por isso a tela **não oferece criar uma forma nova** e **não deixa trocar a
 * forma de um tipo existente**. Não é limitação de tela: o banco recusa pela
 * chave estrangeira composta `fk_promotion_benefit_type_shape`, e trocar a
 * forma reprecificaria em silêncio os anúncios que já estão no ar. Prometer na
 * tela o que o banco vai recusar seria pior do que não oferecer.
 *
 * ## Os `requires*` não são checkboxes soltos
 *
 * `ck_benefit_type_requirements` amarra as exigências de valor à forma - é o
 * que impede um tipo pedir percentual ao lojista e ser calculado como valor
 * fixo. A tela deriva os três da forma escolhida e os mostra como leitura. Só
 * `requiresItemLabel` é escolha livre: um brinde sem o nome do brinde é uma
 * promessa vazia no balcão.
 *
 * ## Desativar não apaga
 *
 * `active = false` tira o tipo do formulário do lojista e **mantém** legível o
 * que já aponta para ele - mesmo comportamento do `active` das cláusulas (V53).
 * É assim que o `DESCRIBED_IN_AD` dos anúncios anteriores ao motor vive hoje.
 */

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const TypeCell = styled.div`
  min-width: 0;

  strong {
    display: block;
    color: ${(p) => p.theme.textPrimary};
  }
  .code {
    font-family: monospace;
    font-size: 0.7rem;
    color: ${(p) => p.theme.textMuted};
    text-transform: uppercase;
  }
  .description {
    font-size: 0.78rem;
    color: ${(p) => p.theme.textSecondary};
    margin-top: 0.15rem;
  }
`;

const Phrase = styled.code`
  font-size: 0.8rem;
  color: ${(p) => p.theme.textSecondary};
  background: ${(p) => p.theme.surfaceAlt};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  padding: 0.15rem 0.4rem;
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.4rem;
  justify-content: flex-end;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const ToggleField = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
  cursor: pointer;
`;

const HelpText = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.75rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textMuted};
`;

/** O bloco que explica a forma de cálculo - leitura, nunca campo, na edição. */
const ShapeBox = styled.div`
  display: grid;
  gap: 0.4rem;
  padding: 0.8rem 0.9rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};

  .head {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    font-size: 0.85rem;
    font-weight: 700;
    color: ${(p) => p.theme.textPrimary};
  }
  .body {
    font-size: 0.78rem;
    line-height: 1.5;
    color: ${(p) => p.theme.textSecondary};
  }
  svg {
    color: ${(p) => p.theme.textMuted};
  }
`;

const Requirements = styled.ul`
  margin: 0.2rem 0 0;
  padding-left: 1.05rem;
  font-size: 0.78rem;
  line-height: 1.6;
  color: ${(p) => p.theme.textSecondary};
`;

const Preview = styled.div`
  padding: 0.75rem 0.9rem;
  border: 1px dashed ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};

  .label {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${(p) => p.theme.textMuted};
  }
  .text {
    margin-top: 0.25rem;
    font-size: 0.95rem;
    color: ${(p) => p.theme.textPrimary};
  }
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.55;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }

  & + & {
    margin-top: 0.75rem;
  }
`;

const SHAPE_OPTIONS = (Object.keys(BENEFIT_VALUE_SHAPE_LABELS) as BenefitValueShape[]).map(
  (value) => ({ value, label: BENEFIT_VALUE_SHAPE_LABELS[value] })
);

const AUDIENCE_OPTIONS = [
  { value: '', label: 'Nenhum (o painel não pré-seleciona)' },
  ...(Object.keys(BENEFIT_AUDIENCE_LABELS) as BenefitAudience[]).map((value) => ({
    value,
    label: BENEFIT_AUDIENCE_LABELS[value],
  })),
];

const STATUS_FILTER = [
  { value: '', label: 'Todos os tipos' },
  { value: 'active', label: 'Ativos' },
  { value: 'inactive', label: 'Inativos' },
];

const CODE_MAX_LENGTH = 40;
const LABEL_MAX_LENGTH = 80;
const TEMPLATE_MAX_LENGTH = 200;

/** Valores de exemplo da pré-visualização - os mesmos marcadores do core. */
const PREVIEW_VALUES: Record<string, string> = {
  '{percent}': '30',
  '{amount}': '25,00',
  '{paid}': '1',
  '{free}': '2',
  '{item}': 'chopp de 300ml',
};

/**
 * Reproduz o `BenefitAmounts.renderLabel` do core **apenas para a
 * pré-visualização desta tela**, com valores de exemplo fixos.
 *
 * Não é uma segunda implementação da aritmética: nenhum cálculo acontece aqui -
 * é substituição de texto, e o que ela responde é "a frase fica legível?", que
 * é a pergunta de quem escreve o template. Quem resolve a frase de verdade,
 * com os valores do anúncio, continua sendo o core.
 */
const renderTemplate = (template: string): string => {
  const resolved = Object.entries(PREVIEW_VALUES).reduce(
    (text, [marker, value]) => text.split(marker).join(value),
    template
  );
  return resolved.trim().replace(/:$/, '').trim().replace(/\s{2,}/g, ' ');
};

interface FormState {
  code: string;
  label: string;
  description: string;
  valueShape: BenefitValueShape;
  labelTemplate: string;
  requiresItemLabel: boolean;
  icon: string;
  defaultAudience: string;
  defaultFlash: boolean;
  active: boolean;
  sortOrder: number;
}

const EMPTY_FORM: FormState = {
  code: '',
  label: '',
  description: '',
  valueShape: 'PERCENT',
  labelTemplate: '',
  requiresItemLabel: false,
  icon: '',
  defaultAudience: '',
  defaultFlash: false,
  active: true,
  sortOrder: 0,
};

const BenefitTypesPage: React.FC = () => {
  const { showNotification } = useNotification();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<BenefitTypeAdminDto | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [removing, setRemoving] = useState<BenefitTypeAdminDto | null>(null);

  const { data, loading, error, reload } = useAsync(
    () => backofficeBenefitTypeApi.list({ size: 200 }),
    []
  );

  const all = useMemo(() => data?.content ?? [], [data]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return all.filter((type) => {
      const matchesTerm =
        !term ||
        [type.label, type.code, type.description ?? '', type.labelTemplate].some((value) =>
          value.toLowerCase().includes(term)
        );
      const matchesStatus =
        statusFilter === '' || (statusFilter === 'active' ? type.active : !type.active);
      return matchesTerm && matchesStatus;
    });
  }, [all, search, statusFilter]);

  const stats = useMemo(
    () => ({
      total: all.length,
      active: all.filter((type) => type.active).length,
      inactive: all.filter((type) => !type.active).length,
      shapes: new Set(all.map((type) => type.valueShape)).size,
    }),
    [all]
  );

  const requirements = BENEFIT_VALUE_SHAPE_REQUIREMENTS[form.valueShape];
  const placeholders = BENEFIT_LABEL_PLACEHOLDERS[form.valueShape];
  /** Quantos anúncios usam o tipo em edição. `undefined` = o core não informou. */
  const usage = editing?.usageCount;
  const inUse = (usage ?? 0) > 0;

  const openCreate = (): void => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, sortOrder: all.length + 1 });
    setSubmitted(false);
    setIsOpen(true);
  };

  const openEdit = (type: BenefitTypeAdminDto): void => {
    setEditing(type);
    setForm({
      code: type.code,
      label: type.label,
      description: type.description ?? '',
      valueShape: type.valueShape,
      labelTemplate: type.labelTemplate,
      requiresItemLabel: type.requiresItemLabel,
      icon: type.icon ?? '',
      defaultAudience: type.defaultAudience ?? '',
      defaultFlash: type.defaultFlash,
      active: type.active,
      sortOrder: type.sortOrder,
    });
    setSubmitted(false);
    setIsOpen(true);
  };

  const update = <K extends keyof FormState>(key: K, value: FormState[K]): void =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const trimmedCode = form.code.trim().toUpperCase();
  const trimmedLabel = form.label.trim();
  const trimmedTemplate = form.labelTemplate.trim();

  const codeError =
    submitted && !editing && trimmedCode === '' ? 'Informe o código do tipo.' : undefined;
  const labelError = submitted && trimmedLabel === '' ? 'Informe o nome do tipo.' : undefined;
  const templateError =
    submitted && trimmedTemplate === '' ? 'Informe a frase que o cliente vai ler.' : undefined;

  const submit = async (): Promise<void> => {
    setSubmitted(true);
    if (trimmedLabel === '' || trimmedTemplate === '' || (!editing && trimmedCode === '')) return;

    const payload: BenefitTypeRequestDto = {
      label: trimmedLabel,
      description: form.description.trim() || undefined,
      labelTemplate: trimmedTemplate,
      requiresItemLabel: form.requiresItemLabel,
      icon: form.icon.trim() || undefined,
      defaultAudience: (form.defaultAudience || undefined) as BenefitAudience | undefined,
      defaultFlash: form.defaultFlash,
      active: form.active,
      sortOrder: form.sortOrder,
      // Só na criação: depois de existir, nem o código nem a forma mudam.
      ...(editing ? {} : { code: trimmedCode, valueShape: form.valueShape }),
    };

    setSubmitting(true);
    try {
      if (editing) {
        await backofficeBenefitTypeApi.update(editing.id, payload);
        showNotification(
          'Tipo atualizado. A frase nova vale para todos os anúncios que apontam para ele - cupons já emitidos mantêm o texto congelado na emissão.',
          'success'
        );
      } else {
        await backofficeBenefitTypeApi.create(payload);
        showNotification('Tipo criado e já disponível no formulário do lojista.', 'success');
      }
      setIsOpen(false);
      reload();
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível salvar o tipo.', BENEFIT_TYPE_ERROR_MESSAGES),
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const confirmRemove = async (): Promise<void> => {
    if (!removing) return;
    setSubmitting(true);
    try {
      await backofficeBenefitTypeApi.remove(removing.id);
      showNotification('Tipo removido do catálogo.', 'success');
      setRemoving(null);
      reload();
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível remover o tipo.', BENEFIT_TYPE_ERROR_MESSAGES),
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Tipos de benefício"
        subtitle="O que o cliente ganha, e como o formulário do lojista pergunta. Abrir um tipo novo dentro de uma forma existente não precisa de deploy."
        actions={
          <Button variant="primary" onClick={openCreate}>
            <Plus size={16} /> Novo tipo
          </Button>
        }
      />

      <StatsGrid>
        <StatCard label="Tipos no catálogo" value={formatNumber(stats.total)} />
        <StatCard label="Ativos" value={formatNumber(stats.active)} />
        <StatCard label="Inativos" value={formatNumber(stats.inactive)} />
        <StatCard label="Formas em uso" value={`${formatNumber(stats.shapes)} de 5`} />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar por nome, código ou frase..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          options={STATUS_FILTER}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        />
        <ActionButton title="Atualizar" onClick={reload}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        isEmpty={filtered.length === 0}
        emptyTitle="Nenhum tipo encontrado"
        emptyDescription="O catálogo nasce com os tipos da migração V84. Crie um novo para abrir uma oferta que ainda não existe."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th style={{ width: '1%', whiteSpace: 'nowrap' }}>Ordem</th>
                <th>Tipo</th>
                <th>Forma de cálculo</th>
                <th>Frase que o cliente lê</th>
                <th>Em uso</th>
                <th style={{ textAlign: 'right' }}>Situação</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((type) => (
                <tr key={type.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{type.sortOrder}</td>
                  <td>
                    <TypeCell>
                      <strong>{type.label}</strong>
                      <div className="code">{type.code}</div>
                      {type.description && <div className="description">{type.description}</div>}
                    </TypeCell>
                  </td>
                  <td>
                    <Badge tone="secondary">{BENEFIT_VALUE_SHAPE_LABELS[type.valueShape]}</Badge>
                  </td>
                  <td>
                    <Phrase>{type.labelTemplate}</Phrase>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {type.usageCount == null ? '-' : `${formatNumber(type.usageCount)} anúncios`}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Badge tone={type.active ? 'success' : 'secondary'}>
                      {type.active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </td>
                  <td>
                    <RowActions>
                      <ActionButton aria-label="Editar tipo" title="Editar" onClick={() => openEdit(type)}>
                        <Edit3 size={16} />
                      </ActionButton>
                      <ActionButton
                        aria-label="Remover tipo"
                        title={
                          (type.usageCount ?? 0) > 0
                            ? 'Em uso por anúncios - desative em vez de remover'
                            : 'Remover'
                        }
                        color="#DC2626"
                        disabled={(type.usageCount ?? 0) > 0}
                        onClick={() => setRemoving(type)}
                      >
                        <Trash2 size={16} />
                      </ActionButton>
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={editing ? 'Editar tipo de benefício' : 'Novo tipo de benefício'}
        width="680px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={submit} isLoading={submitting}>
              Salvar
            </Button>
          </>
        }
      >
        <FormGrid>
          <Row>
            <div>
              <TextInput
                label="Código"
                required
                value={form.code}
                disabled={!!editing}
                maxLength={CODE_MAX_LENGTH}
                error={codeError}
                placeholder="Ex.: VIP_ENTRY"
                onChange={(e) => update('code', e.target.value.toUpperCase())}
              />
              <HelpText>
                {editing
                  ? 'O código não muda: é por ele que o core, o painel e o app falam deste tipo.'
                  : 'Identificador estável, em maiúsculas, sem espaço. Escolhido agora e para sempre.'}
              </HelpText>
            </div>
            <div>
              <TextInput
                label="Ordem"
                type="number"
                value={String(form.sortOrder)}
                onChange={(e) => update('sortOrder', Number(e.target.value) || 0)}
              />
              <HelpText>Posição na lista do formulário do lojista. Menor aparece primeiro.</HelpText>
            </div>
          </Row>

          <TextInput
            label="Nome"
            required
            value={form.label}
            maxLength={LABEL_MAX_LENGTH}
            error={labelError}
            placeholder="Ex.: Entrada VIP"
            onChange={(e) => update('label', e.target.value)}
          />

          <TextInput
            label="Descrição"
            value={form.description}
            maxLength={200}
            placeholder="Uma linha explicando o tipo para quem monta o anúncio."
            onChange={(e) => update('description', e.target.value)}
          />

          {/* A forma é campo só na criação. Depois vira leitura - e o texto diz
              por quê, em vez de deixar o operador descobrir num erro de
              constraint. */}
          {editing ? (
            <ShapeBox>
              <span className="head">
                <Lock size={14} /> {BENEFIT_VALUE_SHAPE_LABELS[form.valueShape]}
              </span>
              <span className="body">
                A forma de cálculo <strong>não muda depois da criação</strong>: ela é a aritmética do
                benefício, e trocá-la reprecificaria em silêncio os anúncios no ar
                {inUse ? ` - e ${formatNumber(usage)} já apontam para este tipo` : ''}. O banco
                também recusa, pela chave estrangeira composta que amarra tipo e forma. Para outra
                forma, crie um tipo novo e desative este.
              </span>
            </ShapeBox>
          ) : (
            <div>
              <Select
                label="Forma de cálculo"
                options={SHAPE_OPTIONS}
                value={form.valueShape}
                onChange={(e) => update('valueShape', e.target.value as BenefitValueShape)}
              />
              <HelpText>
                {BENEFIT_VALUE_SHAPE_HELP[form.valueShape]} Escolha com cuidado: depois de criado, o
                tipo não troca de forma. Formas novas são código - um `BenefitValueShape` e o
                cálculo dele -, não cadastro.
              </HelpText>
            </div>
          )}

          <ShapeBox>
            <span className="head">
              <Info size={14} /> O que o formulário do lojista vai exigir
            </span>
            <span className="body">
              Derivado da forma, não escolhido: é o banco que amarra os dois (`ck_benefit_type_requirements`),
              para o formulário e a conta nunca divergirem.
            </span>
            <Requirements>
              <li>Percentual: {requirements.percent ? 'obrigatório' : 'não é pedido'}</li>
              <li>Valor em reais: {requirements.amount ? 'obrigatório' : 'não é pedido'}</li>
              <li>Unidades (compre X, leve Y): {requirements.units ? 'obrigatório' : 'não é pedido'}</li>
            </Requirements>
          </ShapeBox>

          <div>
            <ToggleField>
              <Switch
                checked={form.requiresItemLabel}
                onChange={(value) => update('requiresItemLabel', value)}
              />
              Exigir o nome do item
            </ToggleField>
            <HelpText>
              A única exigência de escolha livre. Ligue quando o benefício só faz sentido dizendo o
              quê: um "brinde" sem o nome do brinde é uma promessa vazia no balcão.
            </HelpText>
          </div>

          <div>
            <TextInput
              label="Frase que o cliente lê"
              required
              value={form.labelTemplate}
              maxLength={TEMPLATE_MAX_LENGTH}
              error={templateError}
              placeholder="Ex.: Compre {paid}, leve {free}: {item}"
              onChange={(e) => update('labelTemplate', e.target.value)}
            />
            <HelpText>
              Marcadores desta forma: {placeholders.length > 0 ? placeholders.join(' ') : 'nenhum de valor'}
              {form.requiresItemLabel ? ' e {item}' : ''}. Um marcador que a forma não preenche vira
              texto vazio - a frase não quebra, mas fica capenga. Editar aqui corrige o texto em
              todos os anúncios que apontam para o tipo; cupons já emitidos mantêm a frase congelada
              na emissão.
            </HelpText>
          </div>

          <Preview>
            <span className="label">Como fica</span>
            <div className="text">
              {trimmedTemplate ? renderTemplate(trimmedTemplate) : 'Escreva a frase acima.'}
            </div>
            <HelpText>
              Pré-visualização com valores de exemplo (30%, R$ 25,00, 1 leve 2, "chopp de 300ml"). O
              texto real é resolvido pelo core, com os valores do anúncio.
            </HelpText>
          </Preview>

          <Row>
            <div>
              <TextInput
                label="Ícone"
                value={form.icon}
                maxLength={40}
                placeholder="Ex.: package-plus"
                onChange={(e) => update('icon', e.target.value)}
              />
              <HelpText>
                Nome do ícone no lucide, em minúsculas com hífen - é o formato que os tipos da V84
                já usam (`percent`, `gift`, `package-plus`).
              </HelpText>
            </div>
            <div>
              <Select
                label="Público sugerido"
                options={AUDIENCE_OPTIONS}
                value={form.defaultAudience}
                onChange={(e) => update('defaultAudience', e.target.value)}
              />
              <HelpText>
                Apenas pré-seleciona o campo no painel do lojista. Quem decide quem pode resgatar é
                o benefício do anúncio, nunca o tipo.
              </HelpText>
            </div>
          </Row>

          <div>
            <ToggleField>
              <Switch checked={form.defaultFlash} onChange={(value) => update('defaultFlash', value)} />
              Sugerir "relâmpago"
            </ToggleField>
            <HelpText>Pré-marca a contagem regressiva no painel. O lojista pode desmarcar.</HelpText>
          </div>

          <div>
            <ToggleField>
              <Switch checked={form.active} onChange={(value) => update('active', value)} />
              Ativo
            </ToggleField>
            <HelpText>
              Tipo inativo sai do formulário do lojista e <strong>continua legível</strong> nos
              anúncios que já o usam - nada é apagado, e dá para reativar quando quiser.
            </HelpText>
          </div>
        </FormGrid>
      </Modal>

      <Modal
        isOpen={removing !== null}
        onClose={() => setRemoving(null)}
        title="Remover tipo de benefício"
        width="500px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRemoving(null)} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmRemove} isLoading={submitting}>
              Remover
            </Button>
          </>
        }
      >
        <ConfirmText>
          Remover <strong>{removing?.label}</strong> do catálogo?
        </ConfirmText>
        <ConfirmText>
          O servidor recusa se algum anúncio apontar para ele. Na dúvida, prefira{' '}
          <strong>desativar</strong>: o tipo some do formulário do lojista e o que já existe
          continua legível.
        </ConfirmText>
      </Modal>
    </>
  );
};

export default BenefitTypesPage;
